import React, { useState, useEffect, useRef } from 'react';
import logo from '../logo_blade.png';
import { auth, db } from '../services/firebase';
import { RecaptchaVerifier, signInWithPhoneNumber, updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import TermosModal from '../components/TermosModal';

const PAISES = [
  { nome: 'Brasil',         bandeira: '🇧🇷', codigo: '+55',  digitos: 11, mascara: '(##) #####-####' },
  { nome: 'Portugal',       bandeira: '🇵🇹', codigo: '+351', digitos: 9,  mascara: '### ### ###' },
  { nome: 'EUA / Canadá',   bandeira: '🇺🇸', codigo: '+1',   digitos: 10, mascara: '(###) ###-####' },
  { nome: 'Argentina',      bandeira: '🇦🇷', codigo: '+54',  digitos: 10, mascara: '## ####-####' },
  { nome: 'México',         bandeira: '🇲🇽', codigo: '+52',  digitos: 10, mascara: '## ####-####' },
  { nome: 'Colômbia',       bandeira: '🇨🇴', codigo: '+57',  digitos: 10, mascara: '### ###-####' },
  { nome: 'Chile',          bandeira: '🇨🇱', codigo: '+56',  digitos: 9,  mascara: '# ####-####' },
  { nome: 'Peru',           bandeira: '🇵🇪', codigo: '+51',  digitos: 9,  mascara: '### ### ###' },
  { nome: 'Uruguai',        bandeira: '🇺🇾', codigo: '+598', digitos: 8,  mascara: '#### ####' },
  { nome: 'Paraguai',       bandeira: '🇵🇾', codigo: '+595', digitos: 9,  mascara: '### ### ###' },
  { nome: 'Bolívia',        bandeira: '🇧🇴', codigo: '+591', digitos: 8,  mascara: '#### ####' },
  { nome: 'Equador',        bandeira: '🇪🇨', codigo: '+593', digitos: 9,  mascara: '## ### ####' },
  { nome: 'Venezuela',      bandeira: '🇻🇪', codigo: '+58',  digitos: 10, mascara: '####-###-####' },
  { nome: 'Reino Unido',    bandeira: '🇬🇧', codigo: '+44',  digitos: 10, mascara: '#### ######' },
  { nome: 'Espanha',        bandeira: '🇪🇸', codigo: '+34',  digitos: 9,  mascara: '### ### ###' },
  { nome: 'França',         bandeira: '🇫🇷', codigo: '+33',  digitos: 9,  mascara: '## ## ## ## ##' },
  { nome: 'Itália',         bandeira: '🇮🇹', codigo: '+39',  digitos: 10, mascara: '### ### ####' },
  { nome: 'Alemanha',       bandeira: '🇩🇪', codigo: '+49',  digitos: 10, mascara: '#### ######' },
  { nome: 'Japão',          bandeira: '🇯🇵', codigo: '+81',  digitos: 10, mascara: '##-####-####' },
];

function aplicarMascara(digitos, mascara) {
  let result = '';
  let di = 0;
  for (let i = 0; i < mascara.length && di < digitos.length; i++) {
    if (mascara[i] === '#') {
      result += digitos[di++];
    } else {
      result += mascara[i];
    }
  }
  return result;
}

function LoginTelefone({ tipo, onLogin, onVoltar }) {
  const [estagio, setEstagio] = useState('telefone'); // 'telefone' | 'codigo' | 'nome'
  const [pais, setPais] = useState(PAISES[0]);
  const [mostrarPaises, setMostrarPaises] = useState(false);
  const [buscaPais, setBuscaPais] = useState('');
  const [telefoneDigitos, setTelefoneDigitos] = useState('');
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const [termosAceitos, setTermosAceitos] = useState(false);
  const [mostrarTermos, setMostrarTermos] = useState(false);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [reenviarTimer, setReenviarTimer] = useState(0);

  const recaptchaRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);
  const codigoInputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recaptchaVerifierRef.current) {
        try { recaptchaVerifierRef.current.clear(); } catch (_) {}
      }
    };
  }, []);

  useEffect(() => {
    if (estagio === 'codigo' && codigoInputRef.current) {
      setTimeout(() => codigoInputRef.current?.focus(), 300);
    }
  }, [estagio]);

  function iniciarTimer() {
    setReenviarTimer(60);
    timerRef.current = setInterval(() => {
      setReenviarTimer(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }

  function getNumeroE164() {
    return pais.codigo + telefoneDigitos;
  }

  function telefoneValido() {
    return telefoneDigitos.length === pais.digitos;
  }

  async function enviarCodigo() {
    if (!telefoneValido()) {
      setErro(`Digite os ${pais.digitos} dígitos do número`);
      return;
    }
    setErro('');
    setCarregando(true);

    try {
      // Limpa verifier anterior se existir. Além do clear(), zera o DOM do container:
      // o widget renderizado fica "preso" no <div> e, num novo envio, dispara
      // "reCAPTCHA has already been rendered in this element" → timeout → SMS não sai.
      if (recaptchaVerifierRef.current) {
        try { recaptchaVerifierRef.current.clear(); } catch (_) {}
        recaptchaVerifierRef.current = null;
      }
      if (recaptchaRef.current) recaptchaRef.current.innerHTML = '';

      const verifier = new RecaptchaVerifier(auth, recaptchaRef.current, {
        size: 'invisible',
        callback: () => {},
      });
      recaptchaVerifierRef.current = verifier;

      const result = await signInWithPhoneNumber(auth, getNumeroE164(), verifier);
      setConfirmationResult(result);
      setEstagio('codigo');
      iniciarTimer();
    } catch (e) {
      console.error(e);
      if (e.code === 'auth/invalid-phone-number') {
        setErro('Número de telefone inválido');
      } else if (e.code === 'auth/too-many-requests') {
        setErro('Muitas tentativas. Tente novamente mais tarde.');
      } else {
        setErro('Erro ao enviar SMS. Verifique o número e tente novamente.');
      }
    }
    setCarregando(false);
  }

  async function verificarCodigo() {
    if (codigo.length < 6) {
      setErro('Digite o código de 6 dígitos');
      return;
    }
    setErro('');
    setCarregando(true);

    try {
      const result = await confirmationResult.confirm(codigo);
      const user = result.user;

      // Verifica se é usuário novo
      const colecao = tipo === 'barbearia' ? 'barbearias' : 'clientes';
      const docRef = doc(db, colecao, user.uid);
      const snap = await getDoc(docRef);

      if (snap.exists() && snap.data().nome) {
        // Usuário existente — login direto
        const dados = snap.data();
        onLogin({ uid: user.uid, nome: dados.nome, telefone: getNumeroE164() }, false);
      } else {
        // Novo usuário — pede o nome
        setEstagio('nome');
      }
    } catch (e) {
      console.error('Erro verificarCodigo:', e.code, e.message);
      if (e.code === 'auth/invalid-verification-code') {
        setErro('Código incorreto. Verifique e tente novamente.');
      } else if (e.code === 'auth/code-expired') {
        setErro('Código expirado. Reenvie o SMS.');
      } else if (e.code === 'auth/session-expired') {
        setErro('Sessão expirada. Volte e solicite um novo código.');
      } else {
        setErro(`Erro ao verificar código. (${e.code || 'desconhecido'})`);
      }
    }
    setCarregando(false);
  }

  async function salvarNome() {
    if (!nome.trim() || nome.trim().split(' ').filter(p => p).length < 2) {
      setErro('Digite seu nome completo (nome e sobrenome)');
      return;
    }
    if (!termosAceitos) {
      setErro('Você precisa aceitar os Termos de Uso para continuar');
      return;
    }
    setErro('');
    setCarregando(true);

    const termosCampos = { termosAceitos: true, termosAceitosEm: new Date().toISOString() };

    try {
      const user = auth.currentUser;
      await updateProfile(user, { displayName: nome.trim() });

      const colecao = tipo === 'barbearia' ? 'barbearias' : 'clientes';
      const docRef = doc(db, colecao, user.uid);
      if (tipo === 'barbearia') {
        await setDoc(docRef, {
          nome: nome.trim(),
          telefone: getNumeroE164(),
          criadoEm: new Date().toISOString(),
          status: 'pendente',
          ...termosCampos,
        }, { merge: true });
      } else {
        await setDoc(docRef, {
          nome: nome.trim(),
          telefone: getNumeroE164(),
          criadoEm: new Date().toISOString(),
          ...termosCampos,
        }, { merge: true });
      }

      onLogin({ uid: user.uid, nome: nome.trim(), telefone: getNumeroE164() }, true);
    } catch (e) {
      console.error(e);
      setErro('Erro ao salvar. Tente novamente.');
    }
    setCarregando(false);
  }

  async function reenviarCodigo() {
    if (reenviarTimer > 0) return;
    setEstagio('telefone');
    setCodigo('');
    setErro('');
    setConfirmationResult(null);
  }

  const paisesFiltrados = PAISES.filter(p =>
    p.nome.toLowerCase().includes(buscaPais.toLowerCase()) ||
    p.codigo.includes(buscaPais)
  );

  const telefoneMascarado = aplicarMascara(telefoneDigitos, pais.mascara);

  // ── Renderização ──

  return (
    <div style={{
      backgroundColor: '#111111', minHeight: '100vh',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'space-between', fontFamily: 'Arial, sans-serif',
      padding: '60px 24px 40px', position: 'relative'
    }}>
      {/* reCAPTCHA invisível */}
      <div ref={recaptchaRef} />

      <img src={logo} alt="BLADE" style={{ width: '140px', cursor: 'pointer' }} onClick={onVoltar} />

      <div style={{ width: '100%' }}>
        {/* ── ETAPA 1: Telefone ── */}
        {estagio === 'telefone' && (
          <>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#e8e8e8', marginBottom: '6px' }}>
              {tipo === 'barbearia' ? 'Área da Barbearia' : 'Entrar ou Criar conta'}
            </h2>
            <p style={{ fontSize: '13px', color: '#8f8f8f', marginBottom: '32px', lineHeight: '1.6' }}>
              {tipo === 'barbearia'
                ? 'Acesse ou cadastre sua barbearia'
                : 'Informe seu número para receber um código SMS'}
            </p>

            <label style={{ fontSize: '11px', color: '#9c9c9c', letterSpacing: '1px', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
              Número de celular
            </label>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
              {/* Seletor de país */}
              <button
                onClick={() => setMostrarPaises(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '14px 12px', borderRadius: '10px',
                  border: '0.5px solid #2a2a2a', backgroundColor: '#1a1a1a',
                  color: '#e8e8e8', fontSize: '15px', cursor: 'pointer',
                  fontFamily: 'Arial, sans-serif', whiteSpace: 'nowrap', flexShrink: 0
                }}
              >
                <span>{pais.bandeira}</span>
                <span style={{ color: '#aeaeae', fontSize: '13px' }}>{pais.codigo}</span>
                <span style={{ color: '#7d7d7d', fontSize: '10px' }}>▼</span>
              </button>

              {/* Input do número */}
              <input
                type="tel"
                inputMode="numeric"
                placeholder={pais.mascara.replace(/#/g, '0')}
                value={telefoneMascarado}
                onChange={e => {
                  const raw = e.target.value.replace(/\D/g, '').slice(0, pais.digitos);
                  setTelefoneDigitos(raw);
                  setErro('');
                }}
                style={{
                  flex: 1, padding: '14px', borderRadius: '10px',
                  border: '0.5px solid #2a2a2a', backgroundColor: '#1a1a1a',
                  color: '#e8e8e8', fontSize: '15px', boxSizing: 'border-box',
                  outline: 'none', fontFamily: 'Arial, sans-serif', letterSpacing: '1px'
                }}
              />
            </div>

            <p style={{ fontSize: '11px', color: '#7d7d7d', marginBottom: '28px' }}>
              {pais.digitos} dígitos · {pais.nome} · {pais.codigo}
            </p>

            {erro && <p style={{ fontSize: '12px', color: '#e24b4a', marginBottom: '12px' }}>{erro}</p>}

            <button
              onClick={enviarCodigo}
              disabled={carregando || !telefoneValido()}
              style={{
                width: '100%',
                backgroundColor: telefoneValido() && !carregando ? '#2d4a2d' : '#1a2a1a',
                color: telefoneValido() && !carregando ? '#7bc47b' : '#3d5a3d',
                border: 'none', borderRadius: '12px', padding: '16px',
                fontSize: '15px', fontWeight: '600',
                cursor: telefoneValido() && !carregando ? 'pointer' : 'default',
                fontFamily: 'Arial, sans-serif', transition: 'background 0.2s'
              }}
            >
              {carregando ? 'Enviando SMS...' : 'Enviar código por SMS'}
            </button>
          </>
        )}

        {/* ── ETAPA 2: Código OTP ── */}
        {estagio === 'codigo' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
              <button onClick={() => { setEstagio('telefone'); setErro(''); setCodigo(''); }} style={{
                background: 'none', border: 'none', color: '#8f8f8f', cursor: 'pointer',
                fontSize: '20px', padding: '0', lineHeight: '1'
              }}>←</button>
              <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#e8e8e8', margin: 0 }}>
                Código de verificação
              </h2>
            </div>
            <p style={{ fontSize: '13px', color: '#8f8f8f', marginBottom: '32px', lineHeight: '1.6' }}>
              Enviamos um SMS para{' '}
              <span style={{ color: '#7bc47b' }}>{pais.codigo} {telefoneMascarado}</span>
            </p>

            <label style={{ fontSize: '11px', color: '#9c9c9c', letterSpacing: '1px', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
              Código de 6 dígitos
            </label>
            <input
              ref={codigoInputRef}
              type="tel"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={codigo}
              onChange={e => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCodigo(v);
                setErro('');
              }}
              style={{
                width: '100%', padding: '16px', borderRadius: '10px',
                border: '0.5px solid #2a2a2a', backgroundColor: '#1a1a1a',
                color: '#e8e8e8', fontSize: '24px', boxSizing: 'border-box',
                outline: 'none', fontFamily: 'monospace', letterSpacing: '8px',
                textAlign: 'center', marginBottom: '8px'
              }}
            />

            <div style={{ textAlign: 'right', marginBottom: '24px' }}>
              {reenviarTimer > 0 ? (
                <span style={{ fontSize: '12px', color: '#7d7d7d' }}>
                  Reenviar em {reenviarTimer}s
                </span>
              ) : (
                <span onClick={reenviarCodigo} style={{ fontSize: '12px', color: '#7bc47b', cursor: 'pointer' }}>
                  Reenviar código
                </span>
              )}
            </div>

            {erro && <p style={{ fontSize: '12px', color: '#e24b4a', marginBottom: '12px' }}>{erro}</p>}

            <button
              onClick={verificarCodigo}
              disabled={carregando || codigo.length < 6}
              style={{
                width: '100%',
                backgroundColor: codigo.length === 6 && !carregando ? '#2d4a2d' : '#1a2a1a',
                color: codigo.length === 6 && !carregando ? '#7bc47b' : '#3d5a3d',
                border: 'none', borderRadius: '12px', padding: '16px',
                fontSize: '15px', fontWeight: '600',
                cursor: codigo.length === 6 && !carregando ? 'pointer' : 'default',
                fontFamily: 'Arial, sans-serif'
              }}
            >
              {carregando ? 'Verificando...' : 'Verificar código'}
            </button>
          </>
        )}

        {/* ── ETAPA 3: Nome ── */}
        {estagio === 'nome' && (
          <>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#e8e8e8', marginBottom: '6px' }}>
              Qual é o seu nome?
            </h2>
            <p style={{ fontSize: '13px', color: '#8f8f8f', marginBottom: '32px', lineHeight: '1.6' }}>
              {tipo === 'barbearia'
                ? 'Informe o nome do responsável pela barbearia'
                : 'Informe seu nome completo para criar sua conta'}
            </p>

            <label style={{ fontSize: '11px', color: '#9c9c9c', letterSpacing: '1px', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
              Nome completo
            </label>
            <input
              type="text"
              placeholder="Nome e sobrenome"
              value={nome}
              autoFocus
              onChange={e => { setNome(e.target.value); setErro(''); }}
              onKeyDown={e => e.key === 'Enter' && salvarNome()}
              style={{
                width: '100%', padding: '14px', borderRadius: '10px',
                border: '0.5px solid #2a2a2a', backgroundColor: '#1a1a1a',
                color: '#e8e8e8', fontSize: '15px', boxSizing: 'border-box',
                outline: 'none', fontFamily: 'Arial, sans-serif', marginBottom: '16px'
              }}
            />

            {/* Checkbox termos */}
            <div onClick={() => setTermosAceitos(!termosAceitos)}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '0 0 16px', cursor: 'pointer' }}>
              <div style={{
                width: '20px', height: '20px', borderRadius: '6px', flexShrink: 0,
                border: `1.5px solid ${termosAceitos ? '#3d6b3d' : '#333'}`,
                backgroundColor: termosAceitos ? '#2d4a2d' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {termosAceitos && <span style={{ color: '#7bc47b', fontSize: '13px', fontWeight: '700', lineHeight: 1 }}>✓</span>}
              </div>
              <p style={{ fontSize: '13px', color: '#aeaeae', margin: 0, lineHeight: '1.5' }}>
                Li e aceito os{' '}
                <span onClick={e => { e.stopPropagation(); setMostrarTermos(true); }}
                  style={{ color: '#7bc47b', textDecoration: 'underline', cursor: 'pointer' }}>
                  Termos de Uso
                </span>
              </p>
            </div>

            {erro && <p style={{ fontSize: '12px', color: '#e24b4a', marginBottom: '12px' }}>{erro}</p>}

            <button
              onClick={salvarNome}
              disabled={carregando}
              style={{
                width: '100%', backgroundColor: carregando ? '#1a2a1a' : '#2d4a2d',
                color: carregando ? '#3d5a3d' : '#7bc47b',
                border: 'none', borderRadius: '12px', padding: '16px',
                fontSize: '15px', fontWeight: '600', cursor: carregando ? 'default' : 'pointer',
                fontFamily: 'Arial, sans-serif'
              }}
            >
              {carregando ? 'Salvando...' : 'Confirmar'}
            </button>
          </>
        )}
      </div>

      <p style={{ fontSize: '11px', color: '#333', textAlign: 'center' }}>
        BLADE — Agendamento de barbearias
      </p>

      {mostrarTermos && <TermosModal tipo={tipo === 'barbearia' ? 'barbearia' : 'cliente'} onFechar={() => setMostrarTermos(false)} />}

      {/* ── Sheet seletor de países ── */}
      {mostrarPaises && (
        <div
          onClick={() => { setMostrarPaises(false); setBuscaPais(''); }}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)',
            zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: '480px', backgroundColor: '#161616',
              borderRadius: '20px 20px 0 0', padding: '20px 0 32px',
              maxHeight: '70vh', display: 'flex', flexDirection: 'column'
            }}
          >
            <div style={{ padding: '0 20px 16px', borderBottom: '0.5px solid #1e1e1e' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#e8e8e8' }}>Selecionar país</span>
                <span onClick={() => { setMostrarPaises(false); setBuscaPais(''); }} style={{ color: '#8f8f8f', cursor: 'pointer', fontSize: '20px', lineHeight: '1' }}>×</span>
              </div>
              <input
                type="text"
                placeholder="Buscar país ou código..."
                value={buscaPais}
                autoFocus
                onChange={e => setBuscaPais(e.target.value)}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: '8px',
                  border: '0.5px solid #2a2a2a', backgroundColor: '#1a1a1a',
                  color: '#e8e8e8', fontSize: '14px', boxSizing: 'border-box',
                  outline: 'none', fontFamily: 'Arial, sans-serif'
                }}
              />
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {paisesFiltrados.map(p => (
                <div
                  key={p.codigo + p.nome}
                  onClick={() => {
                    setPais(p);
                    setTelefoneDigitos('');
                    setMostrarPaises(false);
                    setBuscaPais('');
                    setErro('');
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '14px 20px', cursor: 'pointer',
                    backgroundColor: pais.nome === p.nome ? '#1a2a1a' : 'transparent',
                    borderBottom: '0.5px solid #191919'
                  }}
                >
                  <span style={{ fontSize: '22px' }}>{p.bandeira}</span>
                  <span style={{ flex: 1, fontSize: '14px', color: '#e8e8e8' }}>{p.nome}</span>
                  <span style={{ fontSize: '13px', color: '#8f8f8f' }}>{p.codigo}</span>
                  {pais.nome === p.nome && <span style={{ color: '#7bc47b', fontSize: '14px' }}>✓</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginTelefone;
